import React, { useState, useEffect } from 'react'
import {
  CouponFormData,
  CouponType,
  EMPTY_COUPON_FORM,
  validateCouponForm,
  AdminCoupon,
} from '../../hooks/useAdminCoupons'

// ── Styles ─────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: 'white',
    borderRadius: '0.75rem',
    padding: '2rem',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  } as React.CSSProperties,
  formGroup: { marginBottom: '1rem' } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '0.3rem',
    fontWeight: '500',
    fontSize: '0.9rem',
    color: '#334155',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  row: {
    display: 'flex',
    gap: '1rem',
  } as React.CSSProperties,
  half: { flex: 1 } as React.CSSProperties,
  btnPrimary: {
    padding: '0.5rem 1rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  btnSecondary: {
    padding: '0.5rem 1rem',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  error: {
    padding: '0.75rem 1rem',
    background: '#fef2f2',
    color: '#dc2626',
    borderRadius: '0.375rem',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  } as React.CSSProperties,
}

/** Formate une date ISO pour un input[type=date] (YYYY-MM-DD) */
const toInputDate = (iso: string | undefined) => iso?.slice(0, 10) ?? ''

// ── Component ──────────────────────────────────────────

type CouponFormModalProps = {
  coupon?: AdminCoupon | null
  couponType?: CouponType
  onSubmit: (form: CouponFormData) => Promise<boolean>
  onClose: () => void
  loading?: boolean
}

const CouponFormModal: React.FC<CouponFormModalProps> = ({
  coupon,
  couponType = 'PARTNER',
  onSubmit,
  onClose,
  loading,
}) => {
  const isEdit = !!coupon

  const [form, setForm] = useState<CouponFormData>(
    coupon
      ? {
          code: coupon.code,
          type: coupon.type ?? couponType,
          valid_from: toInputDate(coupon.valid_from),
          valid_until: toInputDate(coupon.valid_until),
          partner_commission_pct: coupon.partner_commission_pct,
          member_discount_pct: coupon.member_discount_pct,
          user_discount_pct: coupon.user_discount_pct ?? null,
          max_usage_count: coupon.max_usage_count,
          is_active: coupon.is_active,
        }
      : { ...EMPTY_COUPON_FORM, type: couponType }
  )

  const [localError, setLocalError] = useState<string | null>(null)

  const handleChange = (field: keyof CouponFormData, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setLocalError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const err = validateCouponForm(form)
    if (err) {
      setLocalError(err)
      return
    }
    const ok = await onSubmit(form)
    if (ok) onClose()
  }

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div style={styles.overlay} onClick={onClose} data-testid="coupon-modal-overlay">
      <div
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        data-testid="coupon-form-modal"
      >
        <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#1e293b' }}>
          {isEdit ? 'Modifier le coupon' : 'Créer un coupon'}
        </h2>

        {localError && (
          <div style={styles.error} data-testid="form-error">
            {localError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Code */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Code *</label>
            <input
              style={styles.input}
              data-testid="coupon-code-input"
              type="text"
              placeholder="EX: CLUB2026"
              value={form.code}
              onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
              maxLength={30}
              required
            />
          </div>

          {/* Dates */}
          <div style={{ ...styles.formGroup, ...styles.row }}>
            <div style={styles.half}>
              <label style={styles.label}>Début *</label>
              <input
                style={styles.input}
                data-testid="coupon-valid-from"
                type="date"
                value={form.valid_from}
                onChange={(e) => handleChange('valid_from', e.target.value)}
                required
              />
            </div>
            <div style={styles.half}>
              <label style={styles.label}>Fin *</label>
              <input
                style={styles.input}
                data-testid="coupon-valid-until"
                type="date"
                value={form.valid_until}
                onChange={(e) => handleChange('valid_until', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Type indicator */}
          <div style={{ ...styles.formGroup, padding: '0.5rem 0.75rem', background: form.type === 'PARTNER' ? '#eff6ff' : '#fdf4ff', borderRadius: '0.375rem', border: `1px solid ${form.type === 'PARTNER' ? '#bfdbfe' : '#e9d5ff'}` }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: form.type === 'PARTNER' ? '#1d4ed8' : '#7c3aed' }}>
              {form.type === 'PARTNER' ? '🏢 Coupon PARTNER' : '👤 Coupon USER'}
            </span>
          </div>

          {/* Percentages — dynamic by type */}
          {form.type === 'PARTNER' && (
            <div style={{ ...styles.formGroup, ...styles.row }}>
              <div style={styles.half}>
                <label style={styles.label}>Commission partenaire (%)</label>
                <input
                  style={styles.input}
                  data-testid="coupon-commission"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={form.partner_commission_pct}
                  onChange={(e) =>
                    handleChange('partner_commission_pct', parseFloat(e.target.value) || 0)
                  }
                />
              </div>
              <div style={styles.half}>
                <label style={styles.label}>Réduction membre (%)</label>
                <input
                  style={styles.input}
                  data-testid="coupon-discount"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={form.member_discount_pct}
                  onChange={(e) =>
                    handleChange('member_discount_pct', parseFloat(e.target.value) || 0)
                  }
                />
              </div>
            </div>
          )}

          {form.type === 'USER' && (
            <div style={{ ...styles.formGroup, ...styles.row }}>
              <div style={styles.half}>
                <label style={styles.label}>Réduction utilisateur (%)</label>
                <input
                  style={styles.input}
                  data-testid="coupon-user-discount"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={form.user_discount_pct ?? 0}
                  onChange={(e) =>
                    handleChange('user_discount_pct', parseFloat(e.target.value) || 0)
                  }
                />
              </div>
              <div style={styles.half}>
                <label style={styles.label}>Commission partenaire (%) — si partenaire lié</label>
                <input
                  style={styles.input}
                  data-testid="coupon-commission"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={form.partner_commission_pct}
                  onChange={(e) =>
                    handleChange('partner_commission_pct', parseFloat(e.target.value) || 0)
                  }
                />
              </div>
            </div>
          )}

          {/* Max usage */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Utilisations max (vide = illimité)</label>
            <input
              style={styles.input}
              data-testid="coupon-max-usage"
              type="number"
              min={0}
              placeholder="illimité"
              value={form.max_usage_count ?? ''}
              onChange={(e) =>
                handleChange(
                  'max_usage_count',
                  e.target.value === '' ? null : parseInt(e.target.value, 10)
                )
              }
            />
          </div>

          {/* Active */}
          <div style={{ ...styles.formGroup, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              data-testid="coupon-is-active"
              checked={form.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              id="coupon-active"
            />
            <label htmlFor="coupon-active" style={{ ...styles.label, marginBottom: 0 }}>
              Actif
            </label>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
            <button type="button" style={styles.btnSecondary} onClick={onClose}>
              Annuler
            </button>
            <button
              type="submit"
              style={{ ...styles.btnPrimary, opacity: loading ? 0.6 : 1 }}
              disabled={loading}
              data-testid="coupon-submit-btn"
            >
              {loading ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CouponFormModal
